import { useTranslation } from 'react-i18next';
import { Fragment, useMemo } from 'react';

import type { ISummaryCardsProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/analytics-cards';
import { useLangStore } from '@app/dashboard/store/lang';

import { formatAmountWithCurrency } from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';
import { FormatCurrencyForDisplay } from '@unified-checkout/design-system';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { ANALYTIC_CARDS } from '@app/dashboard/constants/balance_and_transfers/transfers/cards';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';

import Cards from './Cards';

import styles from './analytic-cards.module.scss';

const AnalyticCards = ({
  summaryCards,
  isCardTabsLoading,
  isCardTabsError,
}: ISummaryCardsProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);
  const countryCurrency = COUNTRIES_INFO[country]?.currency;

  const formattedCards = useMemo(() => {
    const cards = ANALYTIC_CARDS(t);
    return cards.map((card) => {
      const {
        amount = 0,
        count = 0,
        currency,
      } = summaryCards?.[card.id as keyof typeof summaryCards] || {};

      const formattedAmount = formatAmountWithCurrency({
        amount,
        currency: t(`innerLayout.currencies.${currency ?? countryCurrency}`),
        lang,
      });

      const formattedSupportingText = t(
        'innerLayout.balanceTransfers.transfers.analyticCards.numOfTransfers',
        {
          count,
        }
      );

      return {
        ...card,
        amount: <>{FormatCurrencyForDisplay(formattedAmount)}</>,
        supportingText: formattedSupportingText,
      };
    });
  }, [t, lang, summaryCards]);

  return (
    <div className={styles['analytic-cards']}>
      {formattedCards.map((card) => (
        <Fragment key={card.id}>
          <Cards
            {...card}
            isLoading={isCardTabsLoading}
            isError={isCardTabsError}
            errorText={t('innerLayout.errors.unableToRetrieveData')}
          />
        </Fragment>
      ))}
    </div>
  );
};

export default AnalyticCards;
